import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { useState, useEffect } from "react";

const BASE_URL = "https://oficinavirtual-solponiente.com/api";

function FirmaElectronica({ documento, documentoId, solicitudId, onFirmado }) {
  const [fileBase64, setFileBase64] = useState(null); // Almacena el archivo en Base64 (usuario)
  const [algorithm, setAlgorithm] = useState("SHA256withRSA");
  const [format, setFormat] = useState("PAdES");
  const [showModal, setShowModal] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [signedData, setSignedData] = useState("");
  const [nombreDocumento, setNombreDocumento] = useState("");
  const [descripcionSolicitud, setDescripcionSolicitud] = useState("");
  const [cargandoDocumento, setCargandoDocumento] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const userId = localStorage.getItem("id");
  const userRol = localStorage.getItem("rol");

  useEffect(() => {
    if (window.AutoScript) {
      window.AutoScript.cargarAppAfirma();
    } else {
      console.error("AutoScript no se ha cargado correctamente.");
    }
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNombreDocumento(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(",")[1];
        setFileBase64(base64Data);
      };
      reader.readAsDataURL(file);
    }
  };

  // El documento ya no llega en base64 desde el listado/detalle (por seguridad).
  // Se descarga aquí mismo, justo antes de firmar, a través del endpoint dedicado.
  const obtenerDocumentoBase64 = async () => {
    if (documento) return documento; // compatibilidad por si algún día se sigue pasando directo
    if (!documentoId) {
      alert("No se ha indicado el documento a firmar.");
      return null;
    }
    setCargandoDocumento(true);
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Error al descargar el documento a firmar:", e);
      alert("No se pudo descargar el documento a firmar.");
      return null;
    } finally {
      setCargandoDocumento(false);
    }
  };

  const signDocument = (documentToSign) => {
    if (!documentToSign) {
      alert("No hay documento para firmar.");
      return;
    }

    const extraParams = [
      "signaturePage=1",
      "signaturePositionOnPageLowerLeftX=100",
      "signaturePositionOnPageLowerLeftY=100",
      "signaturePositionOnPageUpperRightX=400",
      "signaturePositionOnPageUpperRightY=200",
      "layer2Text=Firmado por $$SUBJECTCN$$ el día $$SIGNDATE=dd/MM/yyyy$$ con certificado emitido por $$ISSUERCN$$",
      "layer2FontFamily=Arial",
      "layer2FontSize=12",
      "layer2FontColor=000000",
      "layer2FontStyle=0",
    ].join("\n");

    try {
      window.AutoScript.sign(
        documentToSign,
        algorithm,
        format,
        extraParams,
        async (signedData) => {
          setSignedData(signedData);
          setShowModal(true);
        },
        (type, message) => {
          console.error("Error al firmar:", message);
          alert("Error al firmar: " + message);
        }
      );
    } catch (e) {
      console.error("Error al invocar AutoScript para firmar:", e);
    }
  };

  // Punto de entrada del botón "Firmar Y Enviar"
  const iniciarFirma = async () => {
    if (userRol === "admin") {
      const base64 = await obtenerDocumentoBase64();
      if (base64) signDocument(base64);
    } else {
      signDocument(fileBase64);
    }
  };

  // Crea un documento (solo para usuario)
  const sendDocument = async () => {
    const now = new Date();
    const fechaHora = now.toISOString().split("T").join(" ").split("Z")[0];
    const documentData = {
      NombreDocumento: "signed_" + nombreDocumento,
      Documento: signedData,
      usuario_id: userId,
      FirmadoUser: true,
      FechaSubida: fechaHora
    };

    try {
      const response = await fetch(`${BASE_URL}/documentos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(documentData),
      });

      if (response.ok) {
        const result = await response.json();
        sendSolicitud(result.DocumentoID);
      } else {
        alert("Error al guardar el documento.");
      }
    } catch (error) {
      alert("No se pudo conectar con la base de datos.");
    }
  };

  // Crea una solicitud asociada (solo para usuario)
  const sendSolicitud = async (docId) => {
    const now = new Date();
    const fechaHora = now.toISOString().split("T").join(" ").split("Z")[0];
    const solicitudData = {
      UsuarioID: userId,
      DocumentoID: docId,
      Estado: "pendiente",
      FirmadoUser: true,
      Descripcion: descripcionSolicitud,
      FechaSolicitud: fechaHora
    };

    try {
      const response = await fetch(`${BASE_URL}/solicitudes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(solicitudData),
      });

      if (response.ok) {
        alert("Solicitud creada correctamente.");
        setIsSigned(true);
        setShowModal(false);
        // Limpiar el formulario para poder presentar una nueva solicitud
        setFileBase64(null);
        setNombreDocumento("");
        setDescripcionSolicitud("");
        setSignedData("");
        setFileInputKey((k) => k + 1);
        if (onFirmado) onFirmado();
      } else {
        alert("Error al guardar la solicitud.");
      }
    } catch (error) {
      alert("No se pudo conectar con la base de datos.");
    }
  };

  // Actualiza el estado de la solicitud (solo para admin)
  const updateSolicitud = async () => {
    try {
      const response = await fetch(`${BASE_URL}/solicitudes/${solicitudId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ FirmadoAdmin: true, Documento: signedData, NombreDocumento: "signedbyadmin.pdf" }),
      });

      if (response.ok) {
        alert("La solicitud ha sido actualizada y el documento firmado.");
        setIsSigned(true);
        if (onFirmado) onFirmado();
      } else {
        alert("Error al actualizar la solicitud.");
      }
    } catch (error) {
      alert("No se pudo conectar con la base de datos.");
    }
    setShowModal(false);
  };

  return (
    <div>
      {userRol === "usuario" && (
        <>
          <Form.Group controlId="archivo">
            <Form.Label>Añadir archivo</Form.Label>
            <Form.Control key={fileInputKey} type="file" accept=".pdf" onChange={handleFileUpload} />
          </Form.Group>
          <Form.Group controlId="descripcionSolicitud" className="mt-4">
            <Form.Label>Descripción de la solicitud</Form.Label>
            <Form.Control
              type="textarea"
              rows={6}
              placeholder="Escribe la descripción aquí..."
              value={descripcionSolicitud}
              onChange={(e) => setDescripcionSolicitud(e.target.value)}
            />
          </Form.Group>
        </>
      )}

      <div className={userRol === "usuario" ? "mt-4 mb-2" : ""}>
        <Button size="sm" variant="primary" className="w-100" onClick={iniciarFirma} disabled={cargandoDocumento}>
          {cargandoDocumento ? (
            <><Spinner animation="border" size="sm" className="me-2" />Cargando documento…</>
          ) : (
            <><i className="bi bi-vector-pen me-1"></i>Firmar y enviar</>
          )}
        </Button>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Firma completada</Modal.Title>
        </Modal.Header>
        <Modal.Body>El documento se ha firmado correctamente.</Modal.Body>
        <Modal.Footer>

          {userRol === "admin" && signedData && (
            <Button variant="success" className="ms-2" onClick={updateSolicitud}>
              Actualizar Solicitud
            </Button>
          )}
          {userRol === "usuario" && (
            <Button variant="success" className="ms-2" onClick={sendDocument}>
              Enviar
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default FirmaElectronica;
